/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import styled from 'styled-components';
import styles from '../../../styles';

import Localized from '../../../../shared/components/Localized';
import Button from '../../../components/Button';

const Wrapper = styled.div`
`;

const Heading = styled.div`
  ${styles.fonts.headingMedium}
  font-weight: 300;
  margin-bottom: 1rem;
`;

const InputComponent = styled.div`
  margin-bottom: 1rem;
  width: 20rem;
`;

const Label = styled.div`
  ${styles.fonts.headingLabelCaps}
  margin-bottom: 0.25rem;
`;

const CaptchaWrapper = styled.div`
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.div`
  color: #c23934;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

class Form extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      emailAddress: '',
      captchaError: false,
      captchaConfig: {
        enabled: false,
        siteKey: ''
      }
    };

    this.captchaRef = React.createRef();
    this.onEmailChange = this.onEmailChange.bind(this);
    this.submit = this.submit.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
  }

  componentDidMount() {
    // Read captcha config injected at server startup
    const appConfig = window.__APP_CONFIG__ || { captcha: { enabled: false, siteKey: '' } };
    
    // Check if user has captcha_verified cookie (repeat user within 30 days)
    const hasCaptchaCookie = document.cookie.split('; ').find(row => row.startsWith('captcha_verified='));
    const captchaConfig = { ...appConfig.captcha };
    
    // Skip captcha for repeat users with valid cookie
    if (hasCaptchaCookie && captchaConfig.enabled) {
      captchaConfig.enabled = false; // Don't show widget for repeat users
    }
    
    this.setState({ captchaConfig }, () => {
      // Explicitly render reCAPTCHA after config is loaded (only for first-time users)
      if (this.state.captchaConfig.enabled && this.state.captchaConfig.siteKey && window.grecaptcha && window.grecaptcha.render) {
        window.grecaptcha.render(this.captchaRef.current, {
          sitekey: this.state.captchaConfig.siteKey
        });
      }
    });
  }

  onEmailChange(event) {
    this.setState({ emailAddress: event.target.value });
  }

  onKeyPress(event) {
    if (event.key === 'Enter') {
      this.submit();
    }
  }

  submit() {
    const { captchaConfig } = this.state;
    const requestBody = { emailAddress: this.state.emailAddress };
    const requestHeaders = {};
    
    if (captchaConfig.enabled) {
      // Get reCAPTCHA response token
      const captchaToken = window.grecaptcha ? window.grecaptcha.getResponse() : null;
      
      if (!captchaToken) {
        this.setState({ captchaError: true });
        return;
      }
      
      // Send captcha token as header for server-side verification
      requestHeaders['X-Captcha-Token'] = captchaToken;
    }
    
    this.props.loginRequest.handleRequest({ body: requestBody, headers: requestHeaders });
  }

  render() {
    const { captchaConfig } = this.state;

    return (
      <Wrapper>
        <Heading><Localized modelKey='formHeading' modelAttribute='label' modelName='login' /></Heading>
        <InputComponent>
          <Label><Localized modelKey='emailAddress' modelAttribute='label' modelName='login' /></Label>
          <input className='slds-input'
            placeholder='email@example.com'
            value={this.state.emailAddress} onChange={this.onEmailChange} onKeyPress={this.onKeyPress} />
        </InputComponent>
        
        {captchaConfig.enabled && captchaConfig.siteKey && (
          <CaptchaWrapper>
            <div 
              className="g-recaptcha" 
              data-sitekey={captchaConfig.siteKey}
              ref={this.captchaRef}
            />
            {this.state.captchaError && (
              <ErrorMessage>Please complete the captcha verification</ErrorMessage>
            )}
          </CaptchaWrapper>
        )}
        
        <Button theme='brand' onClick={this.submit}>
          <Localized modelKey='emailLinkLogin' modelAttribute='label' modelName='login' />
        </Button>
      </Wrapper>
    );
  }
}

export default Form;
